import { db } from "@/firebase";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc,
  addDoc,
  orderBy,
  setDoc
} from "firebase/firestore";
import { logDebug, logInfo, logError } from "@/utils/secureLogger";

export interface MessagingContact {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  isOnline: boolean;
  lastMessage?: string;
  lastMessageTime?: string;
  lastMessageTimestamp?: Date; // For better sorting
  unreadCount: number;
  canMessage: boolean;
  reason?: string;
  subject?: string; // Add subject field for display
  sessionInfo?: {
    sessionId?: string;
    sessionStatus?: string;
    sessionSubject?: string;
    sessionDate?: string;
  };
  userData: any; // Full user data for reference
}

class MessagingPermissionService {
  private cache = new Map<string, { data: any, timestamp: number }>();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes
  private cleanupInProgress = new Set<string>(); // Track cleanup operations to prevent duplicates

  private getCached(key: string): any | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCache(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }
  
  /**
   * Get tutors that a student can message
   * Only show tutors with existing conversations or confirmed sessions
   */
  async getStudentMessagingContacts(studentId: string): Promise<MessagingContact[]> {
    try {
      logDebug('Getting student messaging contacts', { studentId });
      
      // Check cache first
      const cacheKey = `student_contacts_${studentId}`;
      const cached = this.getCached(cacheKey);
      if (cached) {
        logDebug('Returning cached student messaging contacts', { studentId });
        return cached;
      }
      
      const contacts: MessagingContact[] = [];
      const processedTutorIds = new Set<string>();
      
      // 1. Get tutors with existing conversations
      const conversationsRef = collection(db, "conversations");
      const conversationsQuery = query(
        conversationsRef,
        where("studentId", "==", studentId),
        where("status", "==", "active")
      );

      const conversationsSnapshot = await getDocs(conversationsQuery);
      
      for (const conversationDoc of conversationsSnapshot.docs) {
        const conversationData = conversationDoc.data();
        const tutorId = conversationData.tutorId;
        
        if (processedTutorIds.has(tutorId)) continue;
        processedTutorIds.add(tutorId);
        
        // Get tutor data
        const tutorDoc = await getDoc(doc(db, "users", tutorId));
        if (!tutorDoc.exists()) continue;
        
        const tutorData = tutorDoc.data();
        
        const contact: MessagingContact = {
          id: tutorId,
          name: `${tutorData.firstName} ${tutorData.lastName}`.trim(),
          email: tutorData.email,
          avatar: tutorData.profileImage || "/images/default-avatar.png",
          isOnline: false,
          lastMessage: conversationData.lastMessage || "",
          lastMessageTime: conversationData.lastMessageAt?.toDate().toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit', 
            hour12: true 
          }) || "",
          unreadCount: 0,
          canMessage: true,
          subject: tutorData.subjects?.[0] || "General", // Use tutor's first subject
          userData: tutorData,
          lastMessageTimestamp: conversationData.lastMessageAt?.toDate() || null
        };
        
        contacts.push(contact);
      }
      
      // 2. Get tutors with confirmed sessions (but no existing conversation)
      const sessionsRef = collection(db, "sessions");
      const sessionsQuery = query(
        sessionsRef,
        where("studentId", "==", studentId),
        where("status", "==", "active"),
        where("sessionStatus", "==", "confirmed")
      );

      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      for (const sessionDoc of sessionsSnapshot.docs) {
        const sessionData = sessionDoc.data();
        const tutorId = sessionData.tutorId;
        
        if (processedTutorIds.has(tutorId)) continue;
        processedTutorIds.add(tutorId);
        
        // Get tutor data
        const tutorDoc = await getDoc(doc(db, "users", tutorId));
        if (!tutorDoc.exists()) continue;
        
        const tutorData = tutorDoc.data();
        
        const contact: MessagingContact = {
          id: tutorId,
          name: `${tutorData.firstName} ${tutorData.lastName}`.trim(),
          email: tutorData.email,
          avatar: tutorData.profileImage || "/images/default-avatar.png",
          isOnline: false,
          lastMessage: "",
          lastMessageTime: "",
          unreadCount: 0,
          canMessage: true,
          subject: sessionData.subject || tutorData.subjects?.[0] || "General", // Use session subject or tutor's first subject
          sessionInfo: {
            sessionId: sessionDoc.id,
            sessionStatus: sessionData.sessionStatus,
            sessionSubject: sessionData.subject,
            sessionDate: sessionData.startAt?.toDate().toLocaleDateString()
          },
          userData: tutorData,
          lastMessageTimestamp: sessionData.startAt?.toDate() || null
        };
        
        contacts.push(contact);
      }
      
      // Sort by last message time (most recent first)
      contacts.sort((a, b) => {
        // Use timestamp if available for more accurate sorting
        if (a.lastMessageTimestamp && b.lastMessageTimestamp) {
          return b.lastMessageTimestamp.getTime() - a.lastMessageTimestamp.getTime();
        }
        
        // Fallback to string comparison
        if (a.lastMessageTime && !b.lastMessageTime) return -1;
        if (!a.lastMessageTime && b.lastMessageTime) return 1;
        if (!a.lastMessageTime && !b.lastMessageTime) return a.name.localeCompare(b.name);
        return b.lastMessageTime!.localeCompare(a.lastMessageTime!);
      });
      
      // Cache the results
      this.setCache(cacheKey, contacts);
      
      logInfo('Student messaging contacts loaded', { 
        studentId, 
        contactsCount: contacts.length,
        contacts: contacts.map(c => ({ id: c.id, name: c.name, hasConversation: !!c.lastMessage }))
      });
      
      return contacts;
    } catch (error) {
      logError('Error getting student messaging contacts', error);
      return [];
    }
  }

  /**
   * Get students that a tutor can message
   * Only show students with existing conversations or confirmed sessions
   */
  async getTutorMessagingContacts(tutorId: string): Promise<MessagingContact[]> {
    try {
      logDebug('Getting tutor messaging contacts', { tutorId });
      
      // Check cache first
      const cacheKey = `tutor_contacts_${tutorId}`;
      const cached = this.getCached(cacheKey);
      if (cached) {
        logDebug('Returning cached tutor messaging contacts', { tutorId });
        return cached;
      }
      
      const contacts: MessagingContact[] = [];
      const processedStudentIds = new Set<string>();
      
      // 1. Get students with existing conversations
      const conversationsRef = collection(db, "conversations");
      const conversationsQuery = query(
        conversationsRef,
        where("tutorId", "==", tutorId),
        where("status", "==", "active")
      );

      const conversationsSnapshot = await getDocs(conversationsQuery);
      
      for (const conversationDoc of conversationsSnapshot.docs) {
        const conversationData = conversationDoc.data();
        const studentId = conversationData.studentId;
        
        if (processedStudentIds.has(studentId)) continue;
        processedStudentIds.add(studentId);
        
        // Get student data
        const studentDoc = await getDoc(doc(db, "users", studentId));
        if (!studentDoc.exists()) continue;
        
        const studentData = studentDoc.data();
        
        const contact: MessagingContact = {
          id: studentId,
          name: `${studentData.firstName} ${studentData.lastName}`.trim(),
          email: studentData.email,
          avatar: studentData.profileImage || studentData.avatar || "/images/default-avatar.png",
          isOnline: false,
          lastMessage: conversationData.lastMessage || "",
          lastMessageTime: conversationData.lastMessageAt?.toDate().toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit', 
            hour12: true 
          }) || "",
          unreadCount: 0,
          canMessage: true,
          subject: studentData.subjects?.[0] || "General", // Use student's first subject
          userData: studentData,
          lastMessageTimestamp: conversationData.lastMessageAt?.toDate() || null
        };
        
        contacts.push(contact);
      }
      
      // 2. Get students with confirmed sessions (but no existing conversation)
      const sessionsRef = collection(db, "sessions");
      const sessionsQuery = query(
        sessionsRef,
        where("tutorId", "==", tutorId),
        where("status", "==", "active"),
        where("sessionStatus", "==", "confirmed")
      );

      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      for (const sessionDoc of sessionsSnapshot.docs) {
        const sessionData = sessionDoc.data();
        const studentId = sessionData.studentId;
        
        if (processedStudentIds.has(studentId)) continue;
        processedStudentIds.add(studentId);
        
        // Get student data
        const studentDoc = await getDoc(doc(db, "users", studentId));
        if (!studentDoc.exists()) continue;
        
        const studentData = studentDoc.data();
        
        const contact: MessagingContact = {
          id: studentId,
          name: `${studentData.firstName} ${studentData.lastName}`.trim(),
          email: studentData.email,
          avatar: studentData.profileImage || studentData.avatar || "/images/default-avatar.png",
          isOnline: false,
          lastMessage: "",
          lastMessageTime: "",
          unreadCount: 0,
          canMessage: true,
          subject: sessionData.subject || studentData.subjects?.[0] || "General", // Use session subject or student's first subject
          sessionInfo: {
            sessionId: sessionDoc.id,
            sessionStatus: sessionData.sessionStatus,
            sessionSubject: sessionData.subject,
            sessionDate: sessionData.startAt?.toDate().toLocaleDateString()
          },
          userData: studentData,
          lastMessageTimestamp: sessionData.startAt?.toDate() || null
        };
        
        contacts.push(contact);
      }
      
      // Sort by last message time (most recent first)
      contacts.sort((a, b) => {
        // Use timestamp if available for more accurate sorting
        if (a.lastMessageTimestamp && b.lastMessageTimestamp) {
          return b.lastMessageTimestamp.getTime() - a.lastMessageTimestamp.getTime();
        }
        
        // Fallback to string comparison
        if (a.lastMessageTime && !b.lastMessageTime) return -1;
        if (!a.lastMessageTime && b.lastMessageTime) return 1;
        if (!a.lastMessageTime && !b.lastMessageTime) return a.name.localeCompare(b.name);
        return b.lastMessageTime!.localeCompare(a.lastMessageTime!);
      });
      
      // Cache the results
      this.setCache(cacheKey, contacts);
      
      logInfo('Tutor messaging contacts loaded', { 
        tutorId, 
        contactsCount: contacts.length,
        contacts: contacts.map(c => ({ id: c.id, name: c.name, hasConversation: !!c.lastMessage }))
      });
      
      return contacts;
    } catch (error) {
      logError('Error getting tutor messaging contacts', error);
      return [];
    }
  }

  /**
   * Check if messaging is allowed between two users
   */
  async canUsersMessage(
    currentUserId: string,
    targetUserId: string,
    currentUserType: 'student' | 'tutor'
  ): Promise<{
    canMessage: boolean;
    reason?: string;
    sessionInfo?: any;
  }> {
    try {
      if (currentUserType === 'student') {
        // Check if student's account is verified
        const studentDoc = await getDoc(doc(db, "users", currentUserId));
        if (!studentDoc.exists()) {
          return { canMessage: false, reason: "Student account not found" };
        }
        
        const studentData = studentDoc.data();
        // Check applicationStatus first, fallback to status for backward compatibility
        const studentStatus = studentData.applicationStatus || studentData.status || 'pending';
        
        // Only allow messaging if status is "active", "Active", "verified", or "approved"
        const allowedStatuses = ['active', 'Active', 'verified', 'approved'];
        if (!allowedStatuses.includes(studentStatus)) {
          return { 
            canMessage: false, 
            reason: "Your account must be verified to send messages" 
          };
        }
        
        // Students can message any approved tutor
        const tutorDoc = await getDoc(doc(db, "users", targetUserId));
        if (!tutorDoc.exists()) {
          return { canMessage: false, reason: "Tutor not found" };
        }
        
        const tutorData = tutorDoc.data();
        if (tutorData.profileStatus !== 'approved') {
          return { canMessage: false, reason: "Tutor is not approved" };
        }
        
        return { canMessage: true };
      } else {
        // Tutors can only message students with active sessions
        const sessionsRef = collection(db, "sessions");
        const sessionsQuery = query(
          sessionsRef,
          where("tutorId", "==", currentUserId),
          where("studentId", "==", targetUserId),
          where("status", "==", "active")
        );

        const sessionsSnapshot = await getDocs(sessionsQuery);
        
        const activeSessions = (sessionsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() })) as any[])
          .filter((session: any) => 
            session.sessionStatus === 'pending' || session.sessionStatus === 'confirmed'
          );

        if (activeSessions.length === 0) {
          return { 
            canMessage: false, 
            reason: "No active sessions with this student" 
          };
        }

        const mostRecentSession = activeSessions[0];
        return {
          canMessage: true,
          sessionInfo: {
            sessionId: mostRecentSession.id,
            sessionStatus: (mostRecentSession as any).sessionStatus,
            sessionSubject: (mostRecentSession as any).subject
          }
        };
      }
    } catch (error) {
      logError('Error checking messaging permissions', error);
      return { canMessage: false, reason: "Error checking permissions" };
    }
  }

  /**
   * Clean up duplicate conversations for the same student-tutor pair
   * This fixes the issue where multiple conversations were created due to sessionId linking
   */
  async cleanupDuplicateConversations(studentId: string, tutorId: string): Promise<void> {
    // Prevent duplicate cleanup operations
    const cleanupKey = `${studentId}-${tutorId}`;
    if (this.cleanupInProgress.has(cleanupKey)) {
      logDebug('Cleanup already in progress for this student-tutor pair', { studentId, tutorId });
      return;
    }
    
    this.cleanupInProgress.add(cleanupKey);
    
    try {
      
      const conversationsRef = collection(db, "conversations");
      
      // Find all conversations between this student and tutor
      const conversationQuery1 = query(
        conversationsRef,
        where("studentId", "==", studentId),
        where("tutorId", "==", tutorId),
        where("status", "==", "active")
      );

      const conversationQuery2 = query(
        conversationsRef,
        where("studentId", "==", tutorId),
        where("tutorId", "==", studentId),
        where("status", "==", "active")
      );

      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(conversationQuery1),
        getDocs(conversationQuery2)
      ]);
      
      const allConversations = [...snapshot1.docs, ...snapshot2.docs];
      
      if (allConversations.length > 1) {
        logInfo('Found duplicate conversations, cleaning up', { 
          studentId, 
          tutorId, 
          conversationCount: allConversations.length 
        });
        
        // Keep the most recent conversation, archive the rest
        const sortedConversations = allConversations.sort((a, b) => {
          const timeA = a.data().createdAt?.toDate() || new Date(0);
          const timeB = b.data().createdAt?.toDate() || new Date(0);
          return timeB.getTime() - timeA.getTime();
        });
        
        // Archive all but the first (most recent) conversation
        const conversationsToArchive = sortedConversations.slice(1);
        
        for (const conversationDoc of conversationsToArchive) {
          try {
            await setDoc(conversationDoc.ref, { status: 'archived' }, { merge: true });
            logInfo('Archived duplicate conversation', { conversationId: conversationDoc.id });
          } catch (error) {
            logError('Failed to archive duplicate conversation', error);
            // Continue with other conversations even if one fails
          }
        }
      }
    } catch (error) {
      logError('Error cleaning up duplicate conversations', error);
    } finally {
      // Always remove from cleanup tracking
      this.cleanupInProgress.delete(cleanupKey);
    }
  }

  /**
   * Get existing conversation between two users
   */
  private async getExistingConversation(studentId: string, tutorId: string): Promise<any> {
    try {
      const conversationsRef = collection(db, "conversations");
      
      // Query for conversations where studentId and tutorId match (in either direction)
      const conversationQuery1 = query(
        conversationsRef,
        where("studentId", "==", studentId),
        where("tutorId", "==", tutorId),
        where("status", "==", "active")
      );

      const conversationQuery2 = query(
        conversationsRef,
        where("studentId", "==", tutorId),
        where("tutorId", "==", studentId),
        where("status", "==", "active")
      );

      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(conversationQuery1),
        getDocs(conversationQuery2)
      ]);
      
      // Check first query result
      if (!snapshot1.empty) {
        const doc = snapshot1.docs[0];
        return { ...doc.data(), id: doc.id };
      }
      
      // Check second query result
      if (!snapshot2.empty) {
        const doc = snapshot2.docs[0];
        return { ...doc.data(), id: doc.id };
      }

      return null;
    } catch (error) {
      logError('Error getting existing conversation', error);
      return null;
    }
  }

  /**
   * Create conversation from session context
   * This is called when message button is clicked on session card
   */
  async createConversationFromSession(
    sessionId: string,
    currentUserId: string
  ): Promise<{
    success: boolean;
    conversationId?: string;
    reason?: string;
  }> {
    try {
      logInfo('Creating conversation from session', { sessionId, currentUserId });
      
      // Get session data
      const sessionDoc = await getDoc(doc(db, "sessions", sessionId));
      if (!sessionDoc.exists()) {
        return {
          success: false,
          reason: "Session not found"
        };
      }

      const sessionData = sessionDoc.data();
      const studentId = sessionData.studentId;
      const tutorId = sessionData.tutorId;

      // Verify the current user is part of this session
      if (currentUserId !== studentId && currentUserId !== tutorId) {
        return {
          success: false,
          reason: "You are not authorized to message for this session"
        };
      }

      // Check if conversation already exists
      const existingConversation = await this.getExistingConversation(studentId, tutorId);
      if (existingConversation) {
        logInfo('Using existing conversation', { 
          conversationId: existingConversation.id, 
          sessionId 
        });
        return {
          success: true,
          conversationId: existingConversation.id,
          reason: "Conversation already exists"
        };
      }

      // Create new conversation (not tied to specific session)
      const conversationData: any = {
        studentId,
        tutorId,
        // Remove sessionId to avoid creating multiple conversations for same student-tutor pair
        // sessionId, // Don't tie conversation to specific session
        status: 'active',
        createdAt: new Date(),
        lastMessageAt: null,
        lastMessage: null
      };

      // Get user names
      const [studentDoc, tutorDoc] = await Promise.all([
        getDoc(doc(db, "users", studentId)),
        getDoc(doc(db, "users", tutorId))
      ]);

      if (studentDoc.exists() && tutorDoc.exists()) {
        const studentData = studentDoc.data();
        const tutorData = tutorDoc.data();
        
        conversationData.studentName = `${studentData.firstName} ${studentData.lastName}`.trim();
        conversationData.tutorName = `${tutorData.firstName} ${tutorData.lastName}`.trim();
      }

      // Save conversation
      const conversationsRef = collection(db, "conversations");
      const docRef = await addDoc(conversationsRef, conversationData);
      
      logInfo('Conversation created successfully from session', { conversationId: docRef.id });
      
      return {
        success: true,
        conversationId: docRef.id
      };
    } catch (error) {
      logError('Error creating conversation from session', error);
      return {
        success: false,
        reason: "Failed to create conversation"
      };
    }
  }
}

// Lazy singleton instance to avoid circular dependency issues
let messagingPermissionServiceInstance: MessagingPermissionService | null = null;

export function getMessagingPermissionService(): MessagingPermissionService {
  if (!messagingPermissionServiceInstance) {
    messagingPermissionServiceInstance = new MessagingPermissionService();
  }
  return messagingPermissionServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const messagingPermissionService = new Proxy({} as MessagingPermissionService, {
  get(target, prop) {
    return getMessagingPermissionService()[prop as keyof MessagingPermissionService];
  }
});
export default MessagingPermissionService;
